import React, { useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import { SelectInput } from '@/modules/base/components/inputs'
import DocumentDrawerPreview from '@/modules/document/components/document-drawer/document-drawer-preview'
import {
  buildInvoicePreviewDocuments,
  InvoicePreviewSource,
  InvoicePreviewDocument,
} from '@/modules/document/utils/invoice-preview-documents'
import useInvoiceArtifacts from './use-invoice-artifacts'
import ThemeInterface from '@/theme-interface'

// Geometry of the right-anchored selector, shared with the reservation it asks the preview title
// row for (titleInsetRight below). Constants, not literals in two places: the dropdown floats OVER
// that row, so a width change here has to move the row's content with it or the overlap is back.
const SELECT_WIDTH = 340
const SELECT_RIGHT_OFFSET = 32
// Gap between the title row's last item (status badge) and the dropdown — the row's own `gap`.
const SELECT_TITLE_GAP = 20

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    position: 'relative',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  // Right-align to the preview pane: the filename + "E-Rechnung gültig" validity badge are
  // left-anchored in the preview title row and grow with the filename length, so a centered
  // dropdown can overlap them. Right-anchoring alone was NOT enough (QA, 2026-08-10): the title row
  // is 100% wide, so at every realistic preview width (measured 520-900px) a long e-invoice name
  // still ran underneath the dropdown and the status badge was covered outright. The row therefore
  // also gets told how much of its right end is taken (titleInsetRight) — then the name ellipsises
  // at the visible edge and the status stays readable.
  selectorContainer: {
    position: 'absolute',
    top: 4,
    right: SELECT_RIGHT_OFFSET,
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  selectorLabel: {
    fontSize: theme.fontSizes.smaller,
    fontWeight: theme.fontWeights.medium,
    color: theme.textMuted,
  },
  documentSelect: {
    width: SELECT_WIDTH,
    background: theme.white,
  },
  previewContainer: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
}))

interface InvoiceArtifactPreviewProps {
  document: InvoicePreviewSource
  /**
   * Whether this component refreshes the artifact set itself. On by default for the review hosts,
   * which load their file once and never re-read it. A caller that already refreshes (the expense
   * drawer wrapper) turns it off so the same file is not fetched twice.
   */
  refreshArtifacts?: boolean
  /**
   * Controls the HOST owns and would otherwise lose on an artifact slide. They are handed straight
   * to the preview's own tool column (`toolbarActions`), so they sit under the download button —
   * the verbatim place the legacy preview item keeps them in. Drawn beside the artifact switcher
   * they read as a stray icon floating over the document, which is what QA saw on 2026-09-21
   * („da ist ein Icon verrutscht"). The expense/revenue edit preview passes its
   * „Dokument entfernen" / „Verknüpfung lösen" button here, because this component replaces the legacy
   * preview item that used to carry it. Hosts that deliberately offer no actions (the detail
   * drawer, the central review page) pass nothing and get nothing.
   */
  actions?: React.ReactNode
}

/**
 * FLAG:useDocuAi — the invoice-artifact switcher (original / XML / validation report / report / attachments)
 * for the central review preview. Reuses the SAME builder (`buildInvoicePreviewDocuments`)
 * and renderer (`DocumentDrawerPreview`) as the Documents drawer carousel, but laid out to fill the
 * review preview pane instead of the drawer (no fixed drawer positioning). Only mounted on the
 * useDocuAi review page, so it carries no flag check itself.
 */
const InvoiceArtifactPreview: React.FC<InvoiceArtifactPreviewProps> = ({ document, refreshArtifacts = true, actions }) => {
  const classes = useStyles()
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null)

  // FLAG:useDocuAi — the hosts (Documents review page, embedded expense review) load their file
  // exactly once and must keep it that way: re-seeding would throw away the reviewer's edits. The
  // artifacts, however, keep arriving after that load (the DMS publishes "ready" before the children
  // are linked), so they are refreshed here — ONLY the artifact fields are taken from the refresh,
  // everything else stays the host's document.
  const { resolved } = useInvoiceArtifacts(refreshArtifacts ? document?.id : null)
  const previewSource = useMemo(() => (resolved
    ? {
      ...document,
      invoiceRelatedFiles: resolved.invoiceRelatedFiles ?? document.invoiceRelatedFiles,
      generatedFiles: resolved.generatedFiles ?? document.generatedFiles,
      validationResult: resolved.validationResult ?? document.validationResult,
    }
    : document), [document, resolved])

  const { displayDocuments, signatureDocument } = useMemo(
    () => buildInvoicePreviewDocuments(previewSource),
    [previewSource],
  )

  const documentOptions = useMemo(
    () => displayDocuments.map(doc => ({
      label: (doc as InvoicePreviewDocument).displayLabel || doc.name,
      value: doc.id,
    })),
    [displayDocuments],
  )
  const activeDocument = displayDocuments.find(doc => doc.id === activeDocumentId) || displayDocuments[0]
  const activeIndex = Math.max(0, displayDocuments.findIndex(doc => doc.id === activeDocument?.id))
  // Only the switcher overlays the title row; with a single document nothing is drawn over it.
  const hasSelector = displayDocuments.length > 1

  return (
    <div className={classes.root}>
      {hasSelector && (
        <div className={classes.selectorContainer}>
          <span className={classes.selectorLabel}>{lang.documents.reviewArtifactSelectLabel}</span>
          <SelectInput
            dataQsId="review-artifact-select"
            name="review-artifact-select"
            className={classes.documentSelect}
            value={activeDocument?.id || ''}
            options={documentOptions}
            allowCreate={false}
            notAsync
            disableSearch={displayDocuments.length < 8}
            useFormik={false}
            handleChange={(option) => setActiveDocumentId(option.value)}
            customSelectOptions={{
              placeholder: '',
              labelKey: 'label',
              valueKey: 'value',
              clearable: false,
              backspaceRemoves: false,
              // No menuWidth: the artifact labels are short (original/XML/validation report/report),
              // so the open menu matches the control width and stays inside the preview pane.
              // A forced-wider menu (was 420 vs the 340 control) overhung the right-anchored
              // selector and bled into the review form column next to it.
            }}
          />
        </div>
      )}
      <div className={classes.previewContainer}>
        <DocumentDrawerPreview
          key={activeDocument?.id}
          document={activeDocument}
          isCarouselSlide={hasSelector}
          signatureDocument={activeIndex === 0 ? signatureDocument : undefined}
          hideValidityBadge
          titleInsetRight={hasSelector ? SELECT_WIDTH + SELECT_RIGHT_OFFSET + SELECT_TITLE_GAP : 0}
          toolbarActions={actions}
        />
      </div>
    </div>
  )
}

export default InvoiceArtifactPreview
